import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, delay, map, switchMap } from 'rxjs/operators';
import { PriceItem } from '../../product-pricing/product-pricing.config';
import { PriceItemSettingControllerService } from '@app/api/api/priceItemSettingController.service';
import { PriceSettingRequestDTO } from '@app/api/model/priceSettingRequestDTO';
import { PriceSettingResVO } from '@app/api/model/priceSettingResVO';
import { PageInfoPriceSettingResVO } from '@app/api/model/pageInfoPriceSettingResVO';
import { LovValueDTO } from '@app/api/model/lovValueDTO';

// Constants for price items with i18n keys
export const GOODS_PRICE_ITEMS = [
	['costPrice', 'goods.pricing.price_types.cost_price'],
	['salesPrice', 'goods.pricing.price_types.sales_price'],
	['activityPrice', 'goods.pricing.price_types.activity_price'],
	['marketPrice', 'goods.pricing.price_types.market_price'],
	['distributionPrice', 'goods.pricing.price_types.distribution_price'],
	['wholesalePrice', 'goods.pricing.price_types.wholesale_price'],
	['clearingPrice', 'goods.pricing.price_types.clearing_price'],
	['JG004', 'goods.pricing.price_types.internal_allocation_price'],
] as const;

// Create a map from the constant
const PRICE_ITEM_MAP = new Map(GOODS_PRICE_ITEMS);

@Injectable({ providedIn: 'root' })
export class ProductPricingService {
	private priceItemsSubject = new BehaviorSubject<PriceItem[]>([]);
	private loadingSubject = new BehaviorSubject<boolean>(false);

	priceItems$ = this.priceItemsSubject.asObservable();
	loading$ = this.loadingSubject.asObservable();

	constructor(
		private http: HttpClient,
		private priceItemSettingService: PriceItemSettingControllerService
	) {}

	/**
	 * Convert API response to internal PriceItem format
	 */
	private mapApiResponseToPriceItem(apiItem: PriceSettingResVO): PriceItem {
		return {
			lovValueId: apiItem.id || 0,
			lovId: 200575028642603008, // Default LOV ID
			lovCode: 'PRICE.ITEM.TYPE',
			value: apiItem.value || '',
			meaning: apiItem.meaning || '',
			valueMeaning: PRICE_ITEM_MAP.get(apiItem.value as any) || '',
			description: apiItem.description || '',
			tenantId: apiItem.tenantId || 0,
			orderSeq: apiItem.orderSeq || 0,
			status: apiItem.status !== undefined ? apiItem.status : 1,
			creationDate: null,
			createdBy: null,
			lastUpdateDate: null,
			lastUpdatedBy: null,
			objectVersionNumber: 1,
			_token: ''
		};
	}

	/**
	 * Convert internal PriceItem to API request format
	 */
	private mapPriceItemToApiRequest(item: PriceItem): PriceSettingRequestDTO {
		return {
			value: item.value,
			meaning: item.meaning,
			description: item.description || '',
			orderSeq: item.orderSeq,
			code: 'PRICE.ITEM.TYPE',
			status: item.status,
			tenantId: item.tenantId
		};
	}

	getPriceItems(
		page: number = 1,
		pageSize: number = 20,
		filters?: any
	): Observable<{ data: PriceItem[]; totalRecords: number; loading: boolean }> {
		this.loadingSubject.next(true);

		// Map filters to API parameters
		const apiFilters = {
			value: filters?.value,
			meaning: filters?.meaning,
			status: filters?.status,
			page: page - 1, // API uses 0-based pagination
			size: pageSize,
			sortBy: 'orderSeq',
			ascending: true
		};

		return this.priceItemSettingService.getAll(
			undefined, // ids
			apiFilters.value,
			apiFilters.meaning,
			undefined, // description
			undefined, // orderSeq
			undefined, // code
			apiFilters.status,
			undefined, // tenantId
			apiFilters.page,
			apiFilters.size,
			apiFilters.sortBy,
			apiFilters.ascending
		).pipe(
					map((response: any) => {
			// Check if response is a Blob (error response)
			if (response instanceof Blob) {
				console.error('Received Blob response - likely an error');
				throw new Error('Blob response received');
			}
			
			// Check if response is the expected format
			if (!response || typeof response !== 'object') {
				console.error('Invalid response format:', response);
				throw new Error('Invalid response format');
			}
			
			// Check if pageContent exists and is an array
			if (!response.pageContent || !Array.isArray(response.pageContent)) {
				console.warn('No pageContent or not an array');
				throw new Error('No pageContent or not an array');
			}
			
			const data = (response.pageContent || []).map((item: PriceSettingResVO) => 
				this.mapApiResponseToPriceItem(item)
			);
			
			// Update local cache with the loaded data
			this.priceItemsSubject.next(data);
			this.loadingSubject.next(false);
			return {
				data,
				totalRecords: response.totalElements || 0,
				loading: false
			};
		}),
			catchError((error) => {
				console.error('API Error in getPriceItems:', error);
				this.loadingSubject.next(false);
				throw error; // Re-throw the error to be handled by the component
			})
		);
	}



	getPriceItemById(lovValueId: number): Observable<PriceItem | null> {
		return this.priceItemSettingService.getById(lovValueId).pipe(
			map((response: any) => {
				// Check if response is a Blob (error response)
				if (response instanceof Blob) {
					console.error('Received Blob response in getPriceItemById');
					return null;
				}
				
				if (!response || typeof response !== 'object') {
					console.error('Invalid response format in getPriceItemById:', response);
					return null;
				}
				
				const mappedItem = this.mapApiResponseToPriceItem(response);
				return mappedItem;
			}),
			catchError((error) => {
				console.error('API Error in getPriceItemById:', error);
				return of(null);
			})
		);
	}

	updatePriceItem(payload: PriceItem): Observable<boolean> {
		const apiRequest = this.mapPriceItemToApiRequest(payload);
		
		return this.priceItemSettingService.update8(payload.lovValueId, apiRequest).pipe(
			map((response: any) => {
				// Check if response is a Blob (error response)
				if (response instanceof Blob) {
					console.error('Received Blob response in updatePriceItem');
					return false;
				}
				
				if (!response || typeof response !== 'object') {
					console.error('Invalid response format in updatePriceItem:', response);
					return false;
				}
				
				// Update local cache with the payload that was sent (not the API response)
				const currentItems = this.priceItemsSubject.value;
				const updatedItems = currentItems.map(item => 
					item.lovValueId === payload.lovValueId ? payload : item
				);
				this.priceItemsSubject.next(updatedItems);
				return true;
			}),
			catchError((error) => {
				console.error('API Error in updatePriceItem:', error);
				return of(false);
			})
		);
	}

	togglePriceItemStatus(lovValueId: number): Observable<boolean> {
		// Get the current item from local cache instead of making an API call
		const currentItems = this.priceItemsSubject.value;
		const currentItem = currentItems.find(item => item.lovValueId === lovValueId);
		
		if (!currentItem) {
			console.error('Item not found in local cache with ID:', lovValueId);
			return of(false);
		}
		
		const toggledItem = {
			...currentItem,
			status: currentItem.status === 1 ? 0 : 1,
			lastUpdateDate: new Date().toISOString()
		};
		
		return this.updatePriceItem(toggledItem);
	}

	// Get price code options for dropdown
	getPriceCodeOptions(): Observable<Array<{ value: string; meaning: string; description?: string | null; orderSeq: number }>> {
		return this.priceItemSettingService.getPriceItemSetting().pipe(
			map((response: any) => {
				// Check if response is a Blob (error response)
				if (response instanceof Blob) {
					console.error('Received Blob response in getPriceCodeOptions');
					throw new Error('Blob response received');
				}
				
				if (!response || !Array.isArray(response)) {
					console.error('Invalid response format in getPriceCodeOptions:', response);
					throw new Error('Invalid response format');
				}
				
				return response.map(item => ({
					value: item.value || '',
					meaning: item.meaning || '',
					description: item.description || '',
					orderSeq: item.orderSeq || 0
				}));
			}),
			catchError((error) => {
				console.error('API Error in getPriceCodeOptions:', error);
				throw error; // Re-throw the error to be handled by the component
			})
		);
	}

	// Create a new Product Pricing entry
	createPriceItem(payload: PriceItem): Observable<boolean> {
		const apiRequest = this.mapPriceItemToApiRequest(payload);
		
		return this.priceItemSettingService.create9(apiRequest).pipe(
			map((response: any) => {
				// Check if response is a Blob (error response)
				if (response instanceof Blob) {
					console.error('Received Blob response in createPriceItem');
					return false;
				}
				
				if (!response || typeof response !== 'object') {
					console.error('Invalid response format in createPriceItem:', response);
					return false;
				}
				
				// Add to local cache
				const newItem = this.mapApiResponseToPriceItem(response);
				const currentItems = this.priceItemsSubject.value;
				this.priceItemsSubject.next([...currentItems, newItem]);
				return true;
			}),
			catchError((error) => {
				console.error('API Error in createPriceItem:', error);
				return of(false);
			})
		);
	}

	deletePriceItem(lovValueId: number): Observable<boolean> {
		return this.priceItemSettingService.deleteById(lovValueId).pipe(
			map((response: any) => {
				// Check if response is a Blob (error response)
				if (response instanceof Blob) {
					console.error('Received Blob response in deletePriceItem');
					return false;
				}
				
				// Remove from local cache
				const currentItems = this.priceItemsSubject.value;
				const updatedItems = currentItems.filter(item => item.lovValueId !== lovValueId);
				this.priceItemsSubject.next(updatedItems);
				return true;
			}),
			catchError((error) => {
				
				// Check if it's actually a successful response with status 200
				if (error.status === 200) {
					// Remove from local cache even if API response format is unexpected
					const currentItems = this.priceItemsSubject.value;
					const updatedItems = currentItems.filter(item => item.lovValueId !== lovValueId);
					this.priceItemsSubject.next(updatedItems);
					return of(true);
				}
				
				return of(false);
			})
		);
	}

	getValueMeaningFromLov(value: string): string {
		// Get meaning from constant map
		return PRICE_ITEM_MAP.get(value as any) || value;
	}
}


